import { client } from '@/lib/sanity'
import type { Product } from '@/types/product'

/**
 * Product service - business logic for products
 * All operations use Sanity.io (no local storage)
 */
export const productService = {
  /**
   * Create a new product
   */
  async createProduct(productData: Omit<Product, '_id' | '_type' | '_createdAt' | '_updatedAt'>): Promise<Product> {
    // Validate prerequisites
    if (productData.productType === 'physical' && !productData.bundleSku) {
      throw new Error('Physical products require a bundle')
    }

    // Prefer direct Sanity client for now; server route can be added later
    const product = await client.create({
      _type: 'product',
      ...productData,
      _createdAt: new Date().toISOString(),
      _updatedAt: new Date().toISOString(),
    })

    return product as Product
  },

  /**
   * Update an existing product
   */
  async updateProduct(productId: string, updates: Partial<Product>): Promise<Product> {
    const product = await client
      .patch(productId)
      .set({
        ...updates,
        _updatedAt: new Date().toISOString(),
      })
      .commit()

    return product as unknown as Product
  },

  /**
   * Delete a product
   */
  async deleteProduct(productId: string): Promise<void> {
    try {
      await client.delete(productId)
    } catch (error) {
      console.error('Failed to delete product:', error)
      throw new Error(`Failed to delete product: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  },
}
